import type { AccountId } from "@redotech/redo-account-id";
import {
  type BulkOneClickPrintPickListPosition,
  PrintableDocument,
} from "@redotech/redo-fulfillment-group-common/print-job";
import { PrinterOptionsSchema } from "@redotech/redo-fulfillment-group-common/printer";
import { PageFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { Workstation } from "@redotech/redo-fulfillment-printing-common/workstation";
import type { ConsolidationCartonLabelSize } from "@redotech/redo-merchant-fulfillment-rpc-packing-station/packing-station";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import type {
  ConsolidationCartonId,
  ConsolidationId,
} from "@redotech/redo-warehouse-id";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";

export const PrintableBatchSchema = z.union([
  z.object({ batchId: z.string(), batchNumber: z.string() }),
  z.object({ fulfillmentGroupIds: z.array(z.string()) }),
]);
export type PrintableBatch = z.infer<typeof PrintableBatchSchema>;

export function getBatchPrintingStatusText(
  batch: Extract<PrintableBatch, { batchNumber: string }>,
): string {
  return `Printing Batch #${batch.batchNumber}...`;
}

export const PrintOptionsSchema = z.object({
  workstationId: z.string().optional(),
  printerId: z.number().optional(),
  printerOptions: PrinterOptionsSchema.optional(),
  templateId: z.string().optional(),
});
export type PrintOptions = z.infer<typeof PrintOptionsSchema>;

export function getParcelBoxLabelPrintOptions(
  workstation: Workstation | undefined,
  selectedPrinterId: number | undefined,
): PrintOptions | undefined {
  if (workstation) {
    return workstation.barcodePrinterId
      ? {
          printerId: workstation.barcodePrinterId,
          printerOptions: workstation.barcodePrinterOptions,
        }
      : undefined;
  }

  return selectedPrinterId ? { printerId: selectedPrinterId } : undefined;
}

export function getConsolidationCartonLabelPrintOptions(
  workstation: Workstation | undefined,
  selectedPrinterId: number | undefined,
  labelSize: ConsolidationCartonLabelSize,
): PrintOptions | undefined {
  if (!workstation) {
    return selectedPrinterId ? { printerId: selectedPrinterId } : undefined;
  }

  if (labelSize === "4x2") {
    return workstation.barcodePrinterId
      ? {
          printerId: workstation.barcodePrinterId,
          printerOptions: workstation.barcodePrinterOptions,
        }
      : undefined;
  }

  const labelPrinter = workstation.selectedPrinters.find(
    (printer) => printer.format === PageFormat.Label && !printer.isPremium,
  );
  return labelPrinter
    ? {
        printerId: labelPrinter.printerId,
        printerOptions: labelPrinter.printerOptions,
      }
    : undefined;
}

export async function printParcelBoxLabelIfConfigured({
  client,
  input,
  workstation,
  selectedPrinterId,
  disabled,
}: {
  client: Pick<RedoOutboundLabelsRpcClient, "getParcelBoxLabel">;
  input: { fulfillmentGroupId: string; parcelIndex: number };
  workstation: Workstation | undefined;
  selectedPrinterId: number | undefined;
  disabled: boolean;
}): Promise<boolean> {
  return printLabelIfConfigured({
    disabled,
    printOptions: getParcelBoxLabelPrintOptions(workstation, selectedPrinterId),
    requestPrint: (printOptions) =>
      client.getParcelBoxLabel({ ...input, ...printOptions }),
  });
}

export async function printConsolidationCartonLabelIfConfigured({
  client,
  input,
  workstation,
  selectedPrinterId,
  disabled,
}: {
  client: Pick<
    RedoOutboundLabelsRpcClient,
    "getConsolidationPackingCartonLabel"
  >;
  input: {
    cartonId: ConsolidationCartonId;
    consolidationId: ConsolidationId;
    labelSize: ConsolidationCartonLabelSize;
    merchantAccountId: AccountId;
  };
  workstation: Workstation | undefined;
  selectedPrinterId: number | undefined;
  disabled: boolean;
}): Promise<boolean> {
  return printLabelIfConfigured({
    disabled,
    printOptions: getConsolidationCartonLabelPrintOptions(
      workstation,
      selectedPrinterId,
      input.labelSize,
    ),
    requestPrint: (printOptions) =>
      client.getConsolidationPackingCartonLabel({ ...input, ...printOptions }),
  });
}

async function printLabelIfConfigured({
  disabled,
  printOptions,
  requestPrint,
}: {
  disabled: boolean;
  printOptions: PrintOptions | undefined;
  requestPrint: (printOptions: PrintOptions) => Promise<unknown>;
}): Promise<boolean> {
  if (disabled) {
    return false;
  }

  if (!printOptions?.printerId) {
    return false;
  }

  await requestPrint(printOptions);
  return true;
}

type PrintBatchRpcClient = Pick<
  RedoOutboundLabelsRpcClient,
  | "printBillOfLading"
  | "printCommercialInvoices"
  | "printLabels"
  | "printLocalPickUpLabels"
  | "printOrderSummaryReport"
  | "printPackingSlip"
  | "printParcelPackingSlips"
  | "printPersonalizedSlip"
  | "printPickList"
  | "printReturnLabels"
>;

export function getBulkOneClickPrintPickListInput(
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition,
) {
  return bulkOneClickPrintPickListPosition
    ? { bulkOneClickPrintPickListPosition }
    : {};
}

export async function doPrintBatch(
  client: PrintBatchRpcClient,
  batch: PrintableBatch,
  document: PrintableDocument,
  {
    workstationId,
    printerId,
    printerOptions,
    bulkOneClickPrintPickListPosition,
    templateId,
    signal,
  }: PrintOptions & {
    bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
    signal?: AbortSignal;
  },
): Promise<{
  url: string;
  batchFailed?: boolean | null;
  shipmentErrorMessages?: string[] | null;
}> {
  const printFast = localStorage.getItem("printFast") === "true";

  switch (document) {
    case PrintableDocument.LABEL:
      return await client.printLabels(
        {
          ...batch,
          workstationId,
          printerId,
          printerOptions,
          ...getBulkOneClickPrintPickListInput(
            bulkOneClickPrintPickListPosition,
          ),
          templateId,
          printFast,
          initiationTime: Date.now(),
        },
        { signal },
      );
    case PrintableDocument.PACKING_SLIP:
      return await client.printPackingSlip(
        {
          ...batch,
          workstationId,
          printerId,
          printerOptions,
          templateId,
          printFast,
          initiationTime: Date.now(),
        },
        { signal },
      );
    case PrintableDocument.PICK_LIST:
      return await client.printPickList(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.ORDER_SUMMARY:
      return await client.printOrderSummaryReport(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.COMMERCIAL_INVOICE:
      return await client.printCommercialInvoices(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.PERSONALIZED_SLIP:
      return await client.printPersonalizedSlip(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.RETURN_LABEL:
      return await client.printReturnLabels(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.LOCAL_PICK_UP_LABEL:
      return await client.printLocalPickUpLabels(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.BILL_OF_LADING:
      return await client.printBillOfLading(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.PARCEL_PACKING_SLIP: {
      if (!("fulfillmentGroupIds" in batch)) {
        throw new Error(
          "Package packing slips require fulfillment group IDs, not a batch ID",
        );
      }
      return await client.printParcelPackingSlips(
        {
          fulfillmentGroupIds: batch.fulfillmentGroupIds,
          workstationId,
          printerId,
          printerOptions,
          templateId,
        },
        { signal },
      );
    }
    default:
      assertNever(document);
  }
}
